import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, Heart, Loader2, LogOut, Wrench } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAlerts } from '../../hooks/useAlerts';
import { canView } from '../../utils/permissions';
import { t } from '../../i18n/t';
import { useAsyncAction } from '../../hooks/useAsyncAction';
import '../LopButton/LopButton.css'; // .lop-btn__spinner
import { ThemeSwitcher } from './ThemeSwitcher';

// Registry of dev tools shown in the "פיתוח והטמעה" top-bar menu. Add future
// tools here — the menu itself needs no other change.
const DEV_TOOLS: ReadonlyArray<{ to: string; labelKey: 'devTools.tools.parseFiles' }> = [
  { to: '/dev-tools/parse-files', labelKey: 'devTools.tools.parseFiles' },
];

// Color the heart by the MAX severity present (1=critical→red, 2=severe→orange,
// 3=light→blue, none/empty → muted gray). This is the at-a-glance signal for
// "is there anything alarming right now" — the count badge is the quantitative
// detail. Tooltip surfaces the total + a "no alerts" message for the empty case.
function classifyMaxSeverity(severities: ReadonlyArray<number>): string {
  if (severities.includes(1)) return 'topbar__heart--critical';
  if (severities.includes(2)) return 'topbar__heart--severe';
  if (severities.includes(3)) return 'topbar__heart--light';
  return 'topbar__heart--empty';
}

export function TopBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const showHeart = canView(user, 'alerts');
  const showDevTools = canView(user, 'devTools');
  // Always call the hook (Rules of Hooks); useAlerts is cheap when the user lacks
  // the permission — the underlying fetch will 403 and `data` stays null.
  const { data } = useAlerts();

  const { count, severityClass } = useMemo(() => {
    if (!showHeart || !data) return { count: 0, severityClass: 'topbar__heart--empty' };
    const sevs = data.map((a) => a.severity);
    return { count: data.length, severityClass: classifyMaxSeverity(sevs) };
  }, [data, showHeart]);

  // "פיתוח והטמעה" dev-tools menu — a floating popover anchored under the
  // wrench button, same portal + fixed-coords recipe as SidebarRecent (escapes
  // any ancestor overflow, keeps working regardless of RTL).
  const [devToolsOpen, setDevToolsOpen] = useState(false);
  const [devToolsCoords, setDevToolsCoords] = useState<{ top: number; right: number } | null>(
    null,
  );
  const devToolsBtnRef = useRef<HTMLButtonElement>(null);
  const devToolsMenuRef = useRef<HTMLDivElement>(null);

  const toggleDevTools = useCallback(() => {
    setDevToolsOpen((prev) => {
      const next = !prev;
      if (next) {
        const el = devToolsBtnRef.current;
        if (el) {
          const r = el.getBoundingClientRect();
          setDevToolsCoords({ top: r.bottom + 6, right: window.innerWidth - r.right });
        }
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!devToolsOpen) return;
    function onDown(e: MouseEvent) {
      const tgt = e.target as Node;
      if (devToolsMenuRef.current?.contains(tgt)) return;
      if (devToolsBtnRef.current?.contains(tgt)) return;
      setDevToolsOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setDevToolsOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [devToolsOpen]);

  useEffect(() => {
    if (!devToolsOpen) return;
    const first = devToolsMenuRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
    first?.focus();
  }, [devToolsOpen]);

  // Navigating away closes the menu.
  useEffect(() => {
    setDevToolsOpen(false);
  }, [location.pathname]);

  const { run: handleLogout, running: loggingOut } = useAsyncAction(async () => {
    await logout();
    navigate('/login', { replace: true });
  });

  return (
    <header className="topbar">
      <Link to="/" className="topbar__logo" aria-label={t('sidebar.home')}>
        LOP
      </Link>
      <div className="topbar__user-section">
        {showDevTools && (
          <button
            ref={devToolsBtnRef}
            type="button"
            className="topbar__devtools-trigger"
            onClick={toggleDevTools}
            aria-label={t('devTools.menuTitle')}
            title={t('devTools.menuTitle')}
            aria-haspopup="menu"
            aria-expanded={devToolsOpen}
          >
            <Wrench size={18} aria-hidden="true" />
          </button>
        )}
        {showDevTools &&
          devToolsOpen &&
          devToolsCoords &&
          createPortal(
            <div
              ref={devToolsMenuRef}
              className="topbar__devtools-menu"
              role="menu"
              aria-label={t('devTools.menuTitle')}
              style={{ top: devToolsCoords.top, right: devToolsCoords.right }}
            >
              <div className="topbar__devtools-menu-title">{t('devTools.menuTitle')}</div>
              {DEV_TOOLS.map((tool) => (
                <Link
                  key={tool.to}
                  to={tool.to}
                  role="menuitem"
                  className="topbar__devtools-menu-item"
                  onClick={() => setDevToolsOpen(false)}
                >
                  {t(tool.labelKey)}
                </Link>
              ))}
            </div>,
            document.body,
          )}
        <button
          type="button"
          className="topbar__guide-trigger"
          onClick={() => navigate('/user-guide')}
          aria-label={t('userGuide.open')}
          title={t('userGuide.open')}
        >
          <BookOpen size={18} aria-hidden="true" />
        </button>
        {showHeart && (
          <Link
            to="/alerts"
            className={`topbar__heart ${severityClass}`}
            aria-label={t('alerts.topbarAriaLabel')}
            title={
              count > 0
                ? `${t('alerts.pageTitle')} (${count})`
                : t('alerts.empty')
            }
          >
            <Heart size={18} aria-hidden="true" />
            {count > 0 && (
              <span className="topbar__heart-badge">{count}</span>
            )}
          </Link>
        )}
        {user && (
          <span className="topbar__user">
            {user.displayName} · {t('topbar.organizationLabel')}{' '}
            {user.currentTenant.displayName}
          </span>
        )}
        <ThemeSwitcher />
        <button
          type="button"
          className="topbar__logout"
          onClick={() => handleLogout()}
          disabled={loggingOut}
          aria-busy={loggingOut ? true : undefined}
        >
          {loggingOut ? <Loader2 size={16} className="lop-btn__spinner" aria-hidden="true" /> : <LogOut size={16} />}
          <span>{t('common.logout')}</span>
        </button>
      </div>
    </header>
  );
}
